import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Badge, IconButton, Popover, Box, Typography, List, ListItemButton,
    Divider, Button, Chip,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import NotificationService from '../services/NotificationService';

const TYPE_META = {
    // ── Event-based ──────────────────────────────────────────────────────
    QUOTE_SIGNED:          { label: 'Quote Signed',         color: '#4caf50' },
    QUOTE_REJECTED:        { label: 'Quote Rejected',        color: '#f44336' },
    QUOTE_SENT_TO_PORTAL:  { label: 'Quote Sent',           color: '#2196f3' },
    QUOTE_CHANGE_REQUEST:  { label: 'Change Request',        color: '#ff9800' },
    CONTRACT_SIGNED:       { label: 'Contract Signed',       color: '#2196f3' },
    INVOICE_PAID:          { label: 'Invoice Paid',          color: '#4caf50' },
    REPORT_SIGNED:         { label: 'Report Signed',         color: '#4caf50' },
    PORTAL_NOTE:           { label: 'Customer Message',      color: '#ff9800' },
    // ── Scheduled ────────────────────────────────────────────────────────
    INVOICE_OVERDUE:       { label: 'Invoice Overdue',       color: '#f44336' },
    QUOTE_EXPIRING:        { label: 'Quote Expiring',        color: '#ff9800' },
    CONTRACT_EXPIRING:     { label: 'Contract Expiring',     color: '#ff9800' },
};

const ENTITY_ROUTES = {
    QUOTE:    (id) => `/quote-info/${id}`,
    CONTRACT: (id) => `/contracts/${id}`,
    INVOICE:  (id) => `/invoices/${id}`,
    PROJECT:  (id) => `/project-info/${id}`,
    REPORT:   (id) => `/reports/${id}`,
};

const formatTime = (date) => {
    const diff = (Date.now() - new Date(date)) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return new Date(date).toLocaleDateString();
};

const NotificationBell = () => {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState(null);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    const fetchUnreadCount = useCallback(async () => {
        try {
            const res = await NotificationService.getUnreadCount();
            setUnreadCount(res.data.count);
        } catch (_) {}
    }, []);

    const fetchAll = useCallback(async () => {
        try {
            const res = await NotificationService.getAll();
            setNotifications(res.data);
            setUnreadCount(res.data.filter((n) => !n.isRead).length);
        } catch (_) {}
    }, []);

    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000);
        return () => clearInterval(interval);
    }, [fetchUnreadCount]);

    const handleOpen = (e) => {
        setAnchorEl(e.currentTarget);
        fetchAll();
    };

    const handleClose = () => setAnchorEl(null);

    const handleClick = async (n) => {
        if (!n.isRead) {
            await NotificationService.markRead(n.id).catch(() => {});
            setNotifications((prev) =>
                prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x))
            );
            setUnreadCount((prev) => Math.max(0, prev - 1));
        }
        handleClose();
        if (n.entityType && n.entityId) {
            const route = ENTITY_ROUTES[n.entityType];
            if (route) navigate(route(n.entityId));
        }
    };

    const handleMarkAllRead = async () => {
        await NotificationService.markAllRead().catch(() => {});
        setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
        setUnreadCount(0);
    };

    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton onClick={handleOpen} size="small" sx={{ color: '#7f8c9a' }}>
                <Badge badgeContent={unreadCount || null} color="error" max={99}>
                    {unreadCount > 0 ? <NotificationsIcon /> : <NotificationsNoneIcon />}
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{
                    sx: {
                        width: 380,
                        maxHeight: 480,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        px: 2, py: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid #f0f0f0',
                        flexShrink: 0,
                    }}
                >
                    <Typography variant="subtitle2" fontWeight={700}>Notifications</Typography>
                    {unreadCount > 0 && (
                        <Button size="small" onClick={handleMarkAllRead} sx={{ fontSize: '0.75rem', py: 0, color: '#427898' }}>
                            Mark all read
                        </Button>
                    )}
                </Box>

                {/* List */}
                {notifications.length === 0 ? (
                    <Box sx={{ px: 2, py: 5, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">No notifications yet</Typography>
                    </Box>
                ) : (
                    <List disablePadding sx={{ overflowY: 'auto' }}>
                        {notifications.map((n, i) => {
                            const meta = TYPE_META[n.type] || { label: n.type, color: '#888' };
                            return (
                                <React.Fragment key={n.id}>
                                    <ListItemButton
                                        onClick={() => handleClick(n)}
                                        sx={{
                                            px: 2, py: 1.5,
                                            backgroundColor: n.isRead ? 'transparent' : 'rgba(66,120,152,0.05)',
                                            alignItems: 'flex-start',
                                        }}
                                    >
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                <Chip
                                                    label={meta.label}
                                                    size="small"
                                                    sx={{
                                                        height: 18,
                                                        fontSize: '0.65rem',
                                                        fontWeight: 600,
                                                        backgroundColor: meta.color + '22',
                                                        color: meta.color,
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                {!n.isRead && (
                                                    <Box
                                                        sx={{
                                                            width: 6, height: 6,
                                                            borderRadius: '50%',
                                                            backgroundColor: '#427898',
                                                            ml: 'auto',
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                            <Typography
                                                variant="body2"
                                                fontWeight={n.isRead ? 400 : 600}
                                                sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}
                                            >
                                                {n.title}
                                            </Typography>
                                            {n.message && (
                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{
                                                        display: 'block',
                                                        mt: 0.25,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {n.message}
                                                </Typography>
                                            )}
                                            <Typography variant="caption" sx={{ color: '#bbb', mt: 0.5, display: 'block' }}>
                                                {formatTime(n.createdAt)}
                                            </Typography>
                                        </Box>
                                    </ListItemButton>
                                    {i < notifications.length - 1 && <Divider />}
                                </React.Fragment>
                            );
                        })}
                    </List>
                )}
            </Popover>
        </>
    );
};

export default NotificationBell;
